import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { pctChange } from '@marketing-os/shared';
import { cn } from '@/lib/cn';
import { formatPct } from '@/lib/format';
import { Tooltip } from '@/components/ui/Tooltip';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Valor bruto atual e anterior para calcular a variação. */
  current?: number;
  previous?: number;
  /** Em métricas de custo, cair é bom. */
  lowerIsBetter?: boolean;
  hint?: string;
  icon?: ReactNode;
  footer?: ReactNode;
  loading?: boolean;
  className?: string;
}

export function StatTile({
  label,
  value,
  current,
  previous,
  lowerIsBetter = false,
  hint,
  icon,
  footer,
  loading,
  className,
}: StatTileProps) {
  const hasComparison =
    typeof current === 'number' && typeof previous === 'number' && Number.isFinite(previous);
  const delta = hasComparison ? pctChange(current, previous) : null;
  const flat = delta !== null && Math.abs(delta) < 0.5;
  const positive = delta !== null && (lowerIsBetter ? delta < 0 : delta > 0);

  return (
    <div className={cn('surface-card p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
        {icon && <span className="text-muted">{icon}</span>}
      </div>

      {loading ? (
        <div className="mt-3 h-8 w-24 animate-pulse rounded bg-line/70" aria-hidden />
      ) : (
        <p className="mt-2 text-2xl font-semibold tabular-nums text-fg">{value}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        {delta !== null && (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium',
              flat
                ? 'bg-elevated text-muted'
                : positive
                  ? 'bg-success-soft text-success'
                  : 'bg-danger-soft text-danger',
            )}
          >
            {flat ? (
              <ArrowRight className="h-3 w-3" aria-hidden />
            ) : delta > 0 ? (
              <ArrowUpRight className="h-3 w-3" aria-hidden />
            ) : (
              <ArrowDownRight className="h-3 w-3" aria-hidden />
            )}
            {formatPct(Math.abs(delta), 1)}
          </span>
        )}
        {delta !== null && <span className="text-muted">vs. período anterior</span>}
        {hint && !hasComparison && (
          <Tooltip content={hint}>
            <span className="cursor-help text-muted underline decoration-dotted">detalhe</span>
          </Tooltip>
        )}
      </div>

      {footer && <div className="mt-2 text-xs text-muted">{footer}</div>}
    </div>
  );
}
